import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus, FileText, MessageSquare, Eye, Loader2, Building2, Calendar, Send, Bell } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ClientDashboard = () => {
  const { user, token, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [offers, setOffers] = useState([]);
  const [allResponses, setAllResponses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    const fetchData = async () => {
      try {
        // Pobierz ogłoszenia
        const offersRes = await fetch(`${API}/my-offers`, {
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include',
        });
        if (offersRes.ok) {
          const data = await offersRes.json();
          const offersList = data.offers || [];
          setOffers(offersList);
          
          // Pobierz odpowiedzi dla każdego ogłoszenia
          const responsesPromises = offersList.map(async (offer) => {
            try {
              const respRes = await fetch(`${API}/offers/${offer.offer_id}/responses`, {
                headers: { 'Authorization': `Bearer ${token}` },
                credentials: 'include',
              });
              if (respRes.ok) {
                const respData = await respRes.json();
                return (respData.responses || []).map(r => ({
                  ...r,
                  offer_title: offer.title,
                  offer_id: offer.offer_id
                }));
              }
            } catch (e) {
              console.error('Error fetching responses:', e);
            }
            return [];
          });
          
          const allResps = await Promise.all(responsesPromises);
          const flatResponses = allResps.flat().sort((a, b) => 
            new Date(b.created_at) - new Date(a.created_at)
          );
          setAllResponses(flatResponses);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token, isAuthenticated, navigate]);

  const activeOffers = offers.filter(o => o.status === 'active');
  const totalResponses = offers.reduce((sum, o) => sum + (o.responses_count || 0), 0);
  const offersWithResponses = offers.filter(o => (o.responses_count || 0) > 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="client-dashboard">
      <div className="bg-slate-900 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="font-heading font-bold text-2xl md:text-3xl text-white">Witaj, {user?.name}!</h1>
              <p className="text-slate-400 mt-1">Zarządzaj swoimi zleceniami</p>
            </div>
            <Button onClick={() => navigate('/offers/new')} className="bg-orange-500 hover:bg-orange-600" data-testid="add-offer-btn">
              <Plus className="w-4 h-4 mr-2" />Dodaj zlecenie
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Statystyki */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <FileText className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-heading font-bold">{activeOffers.length}</p>
                <p className="text-sm text-slate-500">Aktywnych zleceń</p>
              </div>
            </CardContent>
          </Card>
          <Card className={totalResponses > 0 ? 'ring-2 ring-orange-500' : ''}>
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center relative">
                <MessageSquare className="w-6 h-6 text-orange-600" />
                {totalResponses > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-orange-500 text-white text-xs rounded-full flex items-center justify-center">
                    {totalResponses > 9 ? '9+' : totalResponses}
                  </span>
                )}
              </div>
              <div>
                <p className="text-2xl font-heading font-bold">{totalResponses}</p>
                <p className="text-sm text-slate-500">Otrzymanych ofert</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Bell className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-heading font-bold">{offersWithResponses.length}</p>
                <p className="text-sm text-slate-500">Zleceń z ofertami</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue={totalResponses > 0 ? "responses" : "offers"} className="space-y-6">
          <TabsList className="grid w-full grid-cols-2 max-w-md">
            <TabsTrigger value="offers" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />Moje zlecenia
            </TabsTrigger>
            <TabsTrigger value="responses" className="flex items-center gap-2 relative">
              <MessageSquare className="w-4 h-4" />Otrzymane oferty
              {totalResponses > 0 && (
                <Badge className="bg-orange-500 text-white ml-1">{totalResponses}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Tab: Otrzymane oferty */}
          <TabsContent value="responses">
            <Card>
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-orange-500" />
                  Otrzymane oferty od firm
                </CardTitle>
              </CardHeader>
              <CardContent>
                {allResponses.length === 0 ? (
                  <div className="text-center py-12">
                    <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <h3 className="font-heading font-bold text-xl text-slate-700 mb-2">Brak ofert</h3>
                    <p className="text-slate-500 mb-6">Gdy firmy odpowiedzą na Twoje zlecenia, zobaczysz je tutaj</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {allResponses.map((resp) => (
                      <div key={resp.response_id || resp.id} className="border border-slate-200 rounded-lg p-4 hover:border-orange-200 transition-colors">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                              <Building2 className="w-5 h-5 text-orange-600" />
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">{resp.company_name || resp.responder_name || 'Firma'}</p>
                              <p className="text-xs text-slate-500">{new Date(resp.created_at).toLocaleDateString('pl-PL')}</p>
                            </div>
                          </div>
                          {(resp.price_from || resp.price_to) && (
                            <Badge className="bg-green-100 text-green-700">
                              {resp.price_from && resp.price_to 
                                ? `${resp.price_from} - ${resp.price_to} PLN` 
                                : resp.price_from 
                                  ? `od ${resp.price_from} PLN` 
                                  : `do ${resp.price_to} PLN`}
                            </Badge>
                          )}
                        </div>
                        
                        <Link to={`/offers/${resp.offer_id}`} className="text-sm text-orange-600 hover:text-orange-700 font-medium mb-2 block">
                          Dot. zlecenia: {resp.offer_title}
                        </Link>
                        
                        {resp.message && (
                          <p className="text-slate-600 text-sm mb-3 bg-slate-50 p-3 rounded">{resp.message}</p>
                        )}
                        
                        {resp.proposed_deadline && (
                          <p className="text-xs text-slate-500 mb-3">
                            <Calendar className="w-3 h-3 inline mr-1" />
                            Proponowany termin: {new Date(resp.proposed_deadline).toLocaleDateString('pl-PL')}
                          </p>
                        )}
                        
                        <div className="flex gap-2 mt-3 pt-3 border-t border-slate-100">
                          <Link 
                            to={`/offers/${resp.offer_id}`}
                            className="text-sm text-slate-600 hover:text-slate-800"
                          >
                            <Eye className="w-4 h-4 inline mr-1" />
                            Zobacz ogłoszenie
                          </Link>
                          {resp.responder_id && (
                            <Link 
                              to={`/messages?user=${resp.responder_id}`}
                              className="text-sm text-orange-600 hover:text-orange-700 font-medium ml-auto"
                            >
                              <Send className="w-4 h-4 inline mr-1" />
                              Napisz wiadomość
                            </Link>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab: Moje zlecenia */}
          <TabsContent value="offers">
            <Card>
              <CardHeader>
                <CardTitle className="font-heading">Twoje zlecenia</CardTitle>
              </CardHeader>
              <CardContent>
                {offers.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <h3 className="font-heading font-bold text-xl text-slate-700 mb-2">Nie masz jeszcze żadnych zleceń</h3>
                    <p className="text-slate-500 mb-6">Dodaj swoje pierwsze zlecenie</p>
                    <Button onClick={() => navigate('/offers/new')} className="bg-orange-500 hover:bg-orange-600">
                      <Plus className="w-4 h-4 mr-2" />Dodaj zlecenie
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {offers.map((offer) => (
                      <div key={offer.offer_id} className="border border-slate-200 rounded-lg p-4 hover:border-slate-300 transition-colors">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Badge className={offer.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}>
                              {offer.status === 'active' ? 'Aktywne' : 'Zamknięte'}
                            </Badge>
                            {(offer.responses_count || 0) > 0 && (
                              <Badge className="bg-orange-100 text-orange-700">
                                <MessageSquare className="w-3 h-3 mr-1" />
                                {offer.responses_count} {offer.responses_count === 1 ? 'oferta' : 'ofert'}
                              </Badge>
                            )}
                            <span className="text-xs text-slate-500">{new Date(offer.created_at).toLocaleDateString('pl-PL')}</span>
                          </div>
                          <Button variant="ghost" size="sm" onClick={() => navigate(`/offers/${offer.offer_id}`)}>
                            <Eye className="w-4 h-4" />
                          </Button>
                        </div>
                        <Link to={`/offers/${offer.offer_id}`}>
                          <h3 className="font-heading font-bold text-lg text-slate-900 hover:text-orange-600">{offer.title}</h3>
                        </Link>
                        <p className="text-sm text-slate-600 mt-1 line-clamp-2">{offer.description}</p>
                        <div className="flex items-center gap-4 mt-3 text-sm text-slate-500">
                          <span>{offer.location}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default ClientDashboard;
